import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, Award, Users, TrendingUp, Filter, Calendar } from 'lucide-react'
import { getUserSpottedTeams, getUserProfile } from '../lib/firestore'
import { SpottedTeamCard } from '../components/team/CleanTeamCard'
import { TeamDetailModal } from '../components/team/team-detail-modal'
import type { Team, User } from '../types'

export default function UserTeamsPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  
  const [userTeams, setUserTeams] = useState<(Team & { spottedAt: Date })[]>([])
  const [userProfile, setUserProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false)
  const [spottedTeamsSortBy, setSpottedTeamsSortBy] = useState<'alphabetical' | 'league' | 'date'>('alphabetical')

  useEffect(() => {
    if (userId) {
      loadUserData()
    }
  }, [userId])

  const loadUserData = async () => {
    if (!userId) return

    try {
      setLoading(true)
      setError(null)

      // Load user profile and teams in parallel
      const [profile, teams] = await Promise.all([
        getUserProfile(userId),
        getUserSpottedTeams(userId, 'all-time')
      ])

      setUserProfile(profile)
      setUserTeams(teams)
    } catch (err) {
      console.error('Error loading user data:', err)
      setError('Failed to load user data')
    } finally {
      setLoading(false)
    }
  }

  // Sort spotted teams based on selected sorting option with memoization
  const sortedSpottedTeams = useMemo(() => {
    return [...userTeams].sort((a, b) => {
      switch (spottedTeamsSortBy) {
        case 'alphabetical':
          return (a.name || '').localeCompare(b.name || '')
        case 'league':
          // Sort by league first, then by name within league
          const leagueComparison = (a.league || '').localeCompare(b.league || '')
          return leagueComparison !== 0 ? leagueComparison : (a.name || '').localeCompare(b.name || '')
        case 'date':
          // Sort by spotted date (most recent first)
          const aDate = a.spottedAt ? new Date(a.spottedAt) : new Date(0)
          const bDate = b.spottedAt ? new Date(b.spottedAt) : new Date(0)
          return bDate.getTime() - aDate.getTime()
        default:
          return (a.name || '').localeCompare(b.name || '')
      }
    })
  }, [userTeams, spottedTeamsSortBy])

  // Calculate stats
  const totalSpots = userTeams.length
  const uniqueLeagues = new Set(userTeams.map(team => team.league)).size

  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team)
    setIsTeamModalOpen(true)
  }

  const closeTeamModal = () => {
    setIsTeamModalOpen(false)
    setSelectedTeam(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-6 shadow-lg">
          <div className="container mx-auto px-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Loading...</h1>
                <p className="text-blue-100">Loading user's spotted teams</p>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <div className="animate-spin h-12 w-12 border-4 border-[#42a5f5] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading teams...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-6 shadow-lg">
          <div className="container mx-auto px-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Error</h1>
                <p className="text-blue-100">Failed to load user data</p>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load User</h2>
            <p className="text-gray-600 mb-6">{error || 'User not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-[#42a5f5] text-white px-6 py-2 rounded-lg hover:bg-[#1976d2] transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  const userName = userProfile.displayName || 'User'

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate(-1)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {userName}'s Spotted Teams
              </h1>
              <p className="text-blue-100">
                {totalSpots} teams spotted across {uniqueLeagues} leagues
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Trophy className="h-8 w-8 text-[#42a5f5] mr-3" />
              <div>
                <p className="text-3xl font-bold text-[#42a5f5]">{totalSpots}</p>
                <p className="text-gray-600 text-sm">Teams Spotted</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-[#42a5f5] mr-3" />
              <div>
                <p className="text-3xl font-bold text-[#42a5f5]">{uniqueLeagues}</p>
                <p className="text-gray-600 text-sm">Leagues</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border lg:col-span-1 col-span-2">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-[#42a5f5] mr-3" />
              <div>
                <p className="text-3xl font-bold text-[#42a5f5]">{userName}</p>
                <p className="text-gray-600 text-sm">SportSpot User</p>
              </div>
            </div>
          </div>
        </div>

        {/* Teams Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {userName}'s Spotted Teams
            </h3>
            <div className="flex items-center space-x-3">
              {userTeams.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Sort:</span>
                  <select 
                    value={spottedTeamsSortBy} 
                    onChange={(e) => setSpottedTeamsSortBy(e.target.value as 'alphabetical' | 'league' | 'date')}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#42a5f5] focus:border-[#42a5f5] bg-white"
                  >
                    <option value="alphabetical">Alphabetical (A-Z)</option>
                    <option value="league">By League</option>
                    <option value="date">By Date Spotted</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          
          {userTeams.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-2">
                {userName} hasn't spotted any teams yet.
              </p>
              <p className="text-sm text-gray-400">
                Teams will appear here once they start spotting!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedSpottedTeams.map((team, index) => (
                <SpottedTeamCard
                  key={`${team.id}-${index}`}
                  team={team as Team & { spottedAt: Date; mode?: 'all-time' | 'trip-mode' }}
                  spottedAt={team.spottedAt || new Date()}
                  spottedMode={'all-time'}
                  onViewDetails={handleTeamClick}
                  onRemove={() => {}} // No remove functionality for other users
                  isRemoving={false}
                  showRemoveButton={false} // Hide remove button for other users
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Team Detail Modal */}
      <TeamDetailModal
        team={selectedTeam}
        isOpen={isTeamModalOpen}
        onClose={closeTeamModal}
      />
    </div>
  )
}